import React, { useState } from "react";

import ChatHeader from "@agent-ui/features/chat/components/chat-elements/ChatHeader";
import ManageChats from "@agent-ui/features/chat/components/chat-elements/ManageChats";
import ChatFileDropZone from "@agent-ui/features/chat/components/ChatFileDropZone";
import { useChatSidebar } from "@agent-ui/features/chat/hooks/useChatSidebar";
import { useSandboxFiles } from "@agent-ui/hooks/useSandboxFiles";
import { useSandboxPreviewBaseUrl } from "@agent-ui/store/sandboxNavigationStore";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/tabs/Tabs";
import twMerge from "@replo/design-system/utils/twMerge";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import { ChatFileDropZoneProvider } from "@v2/features/chat/hooks/useChatFileDropZone";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { routes } from "@v2/utils/router";
import { AppWindow, LayoutPanelTop } from "lucide-react";
import { generatePath, Navigate, useSearchParams } from "react-router-dom";

import PagesSection from "./PagesSection";
import PageTemplatesSection from "./PageTemplatesSection";
import { StartChat } from "./start-chat/StartChat";

function HomeDashboard() {
  const projectId = useCurrentProjectId();

  const { agent } = useReploAgent();
  const storePreviewBaseUrl = useSandboxPreviewBaseUrl();

  const previewBaseUrl = storePreviewBaseUrl ?? null;

  const { files, isLoading } = useSandboxFiles({
    agent,
    projectId: projectId ?? null,
  });

  // NOTE (Kevin, 2025-09-18): Track user's explicit tab selection separately from computed default.
  // This handles the timing issue where useState initializer runs before async data loads,
  // so we dynamically compute the default based on whether pages exist.
  const [userSelectedTab, setUserSelectedTab] = useState<
    "pages" | "templates" | null
  >(null);

  const resolvedDefaultTab: "pages" | "templates" =
    !isLoading && files.length === 0 ? "templates" : "pages";

  const selectedTab = userSelectedTab ?? resolvedDefaultTab;

  return (
    <div className="flex flex-col gap-6 h-full">
      <Tabs
        value={selectedTab}
        onValueChange={(value) => {
          if (value === "pages" || value === "templates") {
            setUserSelectedTab(value);
          }
        }}
        className="h-full"
      >
        <div className="flex flex-col items-center gap-6 h-full">
          <TabsList className="shrink-0 w-full md:w-fit">
            <TabsTrigger
              value="pages"
              startEnhancer={<AppWindow className="w-3.5 h-3.5" />}
            >
              Your Pages
            </TabsTrigger>
            <TabsTrigger
              value="templates"
              startEnhancer={<LayoutPanelTop className="w-3.5 h-3.5" />}
            >
              Use a Template
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pages" className="flex-1 min-h-0 w-full">
            <PagesSection
              setSelectedTab={setUserSelectedTab}
              agent={agent}
              previewBaseUrl={previewBaseUrl}
            />
          </TabsContent>

          <TabsContent value="templates" className="flex-1 min-h-0 w-full">
            <PageTemplatesSection />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function Home() {
  const projectId = useCurrentProjectId();

  const [searchParams] = useSearchParams();
  const { open: isChatSidebarOpen } = useChatSidebar();

  const sessionUuidInParams = searchParams.get("sessionUuid");

  if (projectId && searchParams.has("sessionUuid")) {
    const basePath = generatePath(routes.agent.chat, { projectId });
    const path = `${basePath}?sessionUuid=${sessionUuidInParams}`;
    return <Navigate to={path} replace />;
  }

  return (
    <ChatFileDropZoneProvider>
      <ChatFileDropZone className="flex h-full w-full">
        <div
          data-state={isChatSidebarOpen ? "expanded" : "collapsed"}
          aria-hidden={!isChatSidebarOpen}
          className={twMerge(
            "flex h-full flex-shrink-0 flex-col overflow-hidden transition-[width,opacity] duration-300 ease-out data-[state=collapsed]:w-0 data-[state=collapsed]:opacity-0",
            "hidden md:flex w-64 border-r-0.5 border-border rounded-l-xl",
            !isChatSidebarOpen && "border-r-transparent",
          )}
        >
          <div className="flex flex-col min-w-64 h-full">
            <ManageChats />
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col md:px-3 w-screen md:w-full">
          <div className="relative">
            <div className="absolute top-3">
              <ChatHeader showTitle={false} showVersionHistory={false} />
            </div>
          </div>

          <div
            className="flex-1 min-h-0 overflow-y-auto no-scrollbar"
            id="home-scroll-container"
          >
            <div className="w-full max-w-screen-2xl mx-auto flex flex-col h-full px-3 min-w-0 gap-10 md:gap-16 mt-8 md:mt-24">
              <StartChat />
              <HomeDashboard />
            </div>
          </div>
        </div>
      </ChatFileDropZone>
    </ChatFileDropZoneProvider>
  );
}

export default Home;
